import type { MediaItem } from '@/types/feed.type'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

interface LightboxDialogProps {
  mediaItems: MediaItem[]
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  initialIndex?: number
  fullScreen?: boolean
}

export default function LightboxDialog({
  mediaItems,
  isOpen,
  onOpenChange,
  initialIndex = 0,
  fullScreen,
}: LightboxDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  useDeepEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  const handlePrevious = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length,
    )
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset index when closing
      setCurrentIndex(initialIndex)
      // เพิ่ม delay เพื่อป้องกัน backdrop click
      setTimeout(() => {
        onOpenChange(open)
      }, 50)
    } else {
      onOpenChange(open)
    }
  }

  if (mediaItems.length === 0) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTitle />
      <DialogContent
        aria-describedby=""
        className={
          (fullScreen ? '' : 'max-w-2xl') +
          ' px-3 w-[95dvw] flex flex-col items-center justify-center bg-white border border-gray-200'
        }
      >
        <div className="flex flex-col items-center w-full">
          <div
            className="relative w-full flex items-center justify-center"
            style={{ minHeight: 320 }}
          >
            {mediaItems[currentIndex]?.type === 'image' ? (
              <img
                src={mediaItems[currentIndex].url}
                alt={`media-full-${currentIndex}`}
                className="max-h-[70vh] max-w-full rounded-lg object-contain mx-auto"
                onError={(e) => {
                  ;(e.currentTarget as HTMLImageElement).src =
                    '/images/image-error.png'
                }}
              />
            ) : (
              <video
                src={mediaItems[currentIndex].url}
                controls
                autoPlay
                className="max-h-[70vh] max-w-full rounded-lg object-contain mx-auto bg-gray-100"
              />
            )}
            {/* Prev/Next buttons */}
            {mediaItems.length > 1 && (
              <>
                <Button
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-2 text-white"
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePrevious()
                  }}
                  disabled={mediaItems.length <= 1}
                  aria-label="Previous"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </Button>
                <Button
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-2 text-white"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleNext()
                  }}
                  disabled={mediaItems.length <= 1}
                  aria-label="Next"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </>
            )}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {currentIndex + 1} / {mediaItems.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
